import React, { useState } from 'react';
import { Phone, Smartphone, Mail, MapPin, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { companyInfo } from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      setIsSubmitted(true);
      
      // Reset after showing success
      setTimeout(() => {
        setIsSubmitted(false);
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
      }, 5000);
    } catch (err) {
      console.error('Form submission error:', err);
      setError('Sorry, there was a problem submitting your enquiry. Please call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-brand font-semibold text-sm tracking-wider uppercase mb-4 block">
            Get In Touch
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-6">
            Request Your Free Quote Today
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto">
            Ready to reclaim your space from pests? Contact us for a free,
            no-obligation consultation. We're here to help with expert advice
            and rapid response times.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="flex flex-col space-y-4 lg:space-y-0 lg:justify-between lg:h-full">
            {/* Call Us Card */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 lg:flex-1 flex flex-col justify-center">
              <p className="text-neutral-500 text-sm mb-3">Call Us</p>
              <a
                href={`tel:${companyInfo.phoneTel}`}
                className="flex items-center gap-3 text-neutral-900 hover:text-brand transition-colors mb-3"
              >
                <div className="w-12 h-12 bg-brand rounded-lg flex items-center justify-center flex-shrink-0">
                  <Phone className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-semibold">{companyInfo.phone}</span>
              </a>
              <a
                href={`tel:${companyInfo.mobileTel}`}
                className="flex items-center gap-3 text-neutral-900 hover:text-brand transition-colors"
              >
                <div className="w-12 h-12 bg-brand/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-brand" />
                </div>
                <span className="text-xl font-semibold">{companyInfo.mobile}</span>
              </a>
            </div>

            {/* Email Card */}
            <a
              href={`mailto:${companyInfo.email}`}
              className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 hover:border-brand/30 hover:shadow-lg transition-all duration-300 group lg:flex-1 flex items-center gap-4"
            >
              <div className="w-14 h-14 bg-brand/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <Mail className="w-7 h-7 text-brand" />
              </div>
              <div>
                <p className="text-neutral-500 text-sm mb-1">Email Us</p>
                <p className="text-neutral-900 text-xl font-semibold group-hover:text-brand transition-colors">
                  {companyInfo.email}
                </p>
              </div>
            </a>

            {/* Service Area Card */}
            <div className="bg-neutral-50 border border-neutral-200 rounded-xl p-6 lg:flex-1 flex items-center gap-4">
              <div className="w-14 h-14 bg-brand/20 rounded-xl flex items-center justify-center flex-shrink-0">
                <MapPin className="w-7 h-7 text-brand" />
              </div>
              <div>
                <p className="text-neutral-500 text-sm mb-1">Service Area</p>
                <p className="text-neutral-900 text-xl font-semibold">
                  {companyInfo.serviceArea} & Surrounding Areas
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-neutral-50 border border-neutral-200 rounded-2xl p-8">
            {isSubmitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-20 h-20 bg-brand/20 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-brand" />
                </div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-3">
                  Message Sent!
                </h3>
                <p className="text-neutral-600">
                  Thank you for contacting us. We'll get back to you shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <h3 className="text-2xl font-bold text-neutral-900 text-center mb-2">Contact Form</h3>
                {error && (
                  <div className="flex items-center gap-3 bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg" data-testid="contact-form-error">
                    <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    <p>{error}</p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-neutral-700">
                      Your Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                      className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-neutral-700">
                      Phone Number *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="07xxx xxxxxx"
                      className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-neutral-700">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="john@example.com"
                    className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-neutral-700">Service Required *</Label>
                  <Select onValueChange={handleServiceChange} value={formData.service}>
                    <SelectTrigger className="bg-white border-neutral-300 text-neutral-900 focus:border-brand">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-neutral-200">
                      <SelectItem value="bed-bugs" className="text-neutral-900 hover:bg-brand/10">
                        Bed Bug Control
                      </SelectItem>
                      <SelectItem value="rodents" className="text-neutral-900 hover:bg-brand/10">
                        Rodent Control
                      </SelectItem>
                      <SelectItem value="insects" className="text-neutral-900 hover:bg-brand/10">
                        Insect Control
                      </SelectItem>
                      <SelectItem value="wasps" className="text-neutral-900 hover:bg-brand/10">
                        Wasp Treatment
                      </SelectItem>
                      <SelectItem value="birds" className="text-neutral-900 hover:bg-brand/10">
                        Bird Control & Deterrents
                      </SelectItem>
                      <SelectItem value="commercial" className="text-neutral-900 hover:bg-brand/10">
                        Commercial Services
                      </SelectItem>
                      <SelectItem value="other" className="text-neutral-900 hover:bg-brand/10">
                        Other / Not Sure
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-neutral-700">
                    Tell Us About Your Problem
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Please describe your pest problem..."
                    className="bg-white border-neutral-300 text-neutral-900 placeholder:text-neutral-400 focus:border-brand resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-brand hover:bg-brand-dark text-white font-bold py-6 text-lg"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Send Message
                    </span>
                  )}
                </Button>

                <p className="text-neutral-500 text-sm text-center">
                  We typically respond within 2 hours during business hours.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
